import { DropdownMenu } from '@fabric-app/ui/components/DropdownMenu/DropdownMenu';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { ChevronUpIcon } from '@radix-ui/react-icons';
import type React from 'react';
import styled from 'styled-components';
import {
  SearchBarFilterType,
  type SearchBarFilter,
  type SearchBarFilterOption,
} from '@fabric-app/business/search/searchbar.types';
import { usePillAutocomplete } from '../hooks/usePillAutocomplete';
import { searchBarFilterConfigs } from '../searchbar.config';
import { noop } from 'swr/_internal';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { useInputControls } from '@fabric-app/hooks/useInputControls';
import { Flex } from '@fabric-app/ui/components/Flex';
import { TextInput } from '@fabric-app/ui/components/TextInput/TextInput';
import { AnimateChangeInHeight } from '@fabric-app/ui/components/animations/AnimateChangeInHeight';
import { useBoolState } from '@fabric-app/hooks/useBooleanState';
import { useQueryFolders } from '@fabric-app/business/resources/queries/useQueryFolders';
import {
  DropdownItemBase,
  DropdownItemIconContainer,
} from '@fabric-app/ui/components/DropdownMenu/Item';
import { SidebarActionButton } from '@fabric-app/ui/components/button/ButtonExpandCollapse';
import { DrawerTitle } from '@fabric-app/ui/components/Modal/Drawer';
import { renderResourceIcon } from '@fabric-app/business/resource-detail/components/ResourceIcon/renderResourceIcon';

const StyledScrollArea = styled(ScrollArea.Default).attrs((props) => {
  return {
    ...props,
  };
})`
  min-height: 1px;
  display: flex;
  [data-radix-scroll-area-viewport] {
    height: auto;
  }
`;

const shouldSplitGroups = (type: SearchBarFilter['type'], searchQuery: string) => {
  return type === SearchBarFilterType.LOCATION && searchQuery.length > 0;
};

const getPlaceholderByType = (type: SearchBarFilterType) => {
  switch (type) {
    case SearchBarFilterType.TAG:
      return 'Search for a tag';
    case SearchBarFilterType.LOCATION:
      return 'Search for a folder or space';
    case SearchBarFilterType.CONTENT_TYPE:
      return 'Search for a kind of item';
    default:
      return 'Search';
  }
};

export const FilterDropdownMobile: React.FC<{
  filter: SearchBarFilter;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
  open: boolean;
  onChange: (value: SearchBarFilterOption) => void;
}> = ({ filter, onOpenChange, open, onChange }) => {
  const inputControls = useInputControls('', {
    debounce: 300,
  });
  const { options, isLoading } = usePillAutocomplete(filter.type, inputControls.value);

  return (
    <DropdownMenu.Base
      renderTriggerElement={() => null}
      openProps={{
        open,
        onOpenChange,
      }}
    >
      <Flex overflow="hidden" direction="column">
        <DropdownMenu.Group>
          <DrawerTitle>
            Filter by:{' '}
            <span style={{ textTransform: 'capitalize' }}>
              {searchBarFilterConfigs[filter.type].name}
            </span>
          </DrawerTitle>
        </DropdownMenu.Group>
        <DropdownMenu.Group>
          <TextInput
            {...inputControls}
            placeholder={getPlaceholderByType(filter.type)}
            onChange={inputControls.onChange}
            onKeyDown={(e) => {
              /**
               * there are multiple parent listeners to keydown which are blocking the onchange event, not sure which one ...
               * need to stop propagating the event here so the input is updated, otherwise it e.g. looses focus and so on
               */
              e.stopPropagation();
            }}
            startAdornment={<TextInput.SearchIcon />}
          />
          <br />
        </DropdownMenu.Group>

        {isLoading ? (
          <LoadingContainer>
            <Spinner thickness={3} size={24} />
          </LoadingContainer>
        ) : (
          <StyledScrollArea style={{ minHeight: 1, height: '100%' }}>
            <AnimateChangeInHeight duration={0.2}>
              {options.length > 0 ? (
                shouldSplitGroups(filter.type, inputControls.value) ? (
                  options.map((option) => (
                    <DropdownMenu.Group key={option.value}>
                      <Option option={option} onChange={onChange} />
                    </DropdownMenu.Group>
                  ))
                ) : (
                  <DropdownMenu.Group>
                    {options.map((option) => (
                      <Option key={option.value} option={option} onChange={onChange} />
                    ))}
                  </DropdownMenu.Group>
                )
              ) : (
                <DropdownMenu.Group>
                  <LoadingContainer>No option found</LoadingContainer>
                </DropdownMenu.Group>
              )}
            </AnimateChangeInHeight>
          </StyledScrollArea>
        )}
      </Flex>
    </DropdownMenu.Base>
  );
};

const Option: React.FC<{
  option: SearchBarFilterOption;
  onChange: (value: SearchBarFilterOption) => void;
  level?: number;
}> = ({ option, onChange, level = 0 }) => {
  const isExpandedState = useBoolState();

  const { folders: folders, isLoading } = useQueryFolders(option.value, {
    enabled: isExpandedState.value && option.canHaveChildren,
  });

  const icon = option.icon;
  const itemOption = (
    <DropdownMenu.Item
      key={option.value}
      onSelect={noop}
      onClick={() => {
        onChange(option);
      }}
      style={{ paddingLeft: `${level * 20 + 16}px` }}
    >
      {icon && (
        <DropdownItemIconContainer style={{ color: cssVar['color-text-quaternary'] }}>
          {icon}
        </DropdownItemIconContainer>
      )}
      {option.label}
      {option.canHaveChildren && (
        <SidebarActionButton
          onClick={(e) => {
            e.stopPropagation();
            isExpandedState.handleToggle();
          }}
          style={{ marginLeft: 'auto' }}
        >
          <ChevronUpIcon
            style={{
              height: 14,
              width: 14,
              transition: '.2s rotate',
              rotate: isExpandedState.value ? '0deg' : '180deg',
            }}
          />
        </SidebarActionButton>
      )}
    </DropdownMenu.Item>
  );

  if (option.canHaveChildren) {
    return (
      <>
        {itemOption}
        {isExpandedState.value ? (
          isLoading ? (
            <LoadingContainer>
              <Spinner thickness={3} size={24} />
            </LoadingContainer>
          ) : folders.length === 0 ? (
            <LoadingContainer>No subfolders</LoadingContainer>
          ) : (
            folders.map((folder) => (
              <Option
                key={folder.id}
                option={{
                  label: folder.name || 'Untitled folder',
                  value: folder.id,
                  canHaveChildren: true,
                  type: 'folder',
                  icon: renderResourceIcon(folder, { size: 24 }),
                }}
                onChange={onChange}
                level={level + 1}
              />
            ))
          )
        ) : null}
      </>
    );
  }

  return itemOption;
};

const LoadingContainer = styled(DropdownItemBase).attrs((props) => {
  return {
    ...props,
    as: 'div',
  };
})`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  font-size: 0.75rem;
  color: ${cssVar['color-text-tertiary']};
`;
